/**
 * Health → Data loads.
 *
 * Lets the user import data from third-party sources (today: RENPHO Health
 * CSV exports; tomorrow: Garmin / MyFitnessPal / Apple Health / …). The
 * screen is split into three parts:
 *
 *   1. Source picker      — cards listing every available loader.
 *   2. File upload        — drag-and-drop / picker; runs the load on submit.
 *   3. History            — past load runs with their summary at a glance.
 *
 * Post-upload, a modal pops up showing the full summary: created /
 * skipped / errored with a truncated error list. The same modal is also
 * reachable by clicking a row in the history table.
 */

import { useRef, useState } from 'react';
import { Icon } from '@k-os/ui';
import {
  useDataLoadSources,
  useDataLoads,
  useRunDataLoad,
  type DataLoadSource,
  type DataLoadSummary,
  type RunDataLoadResponse,
  type DataLoadRunDto,
} from '../../api/health';
import styles from './health.module.css';

export function HealthDataLoadsScreen() {
  const sourcesQ = useDataLoadSources();
  const loadsQ = useDataLoads();
  const run = useRunDataLoad();

  const sources = sourcesQ.data?.sources ?? [];
  const loads = loadsQ.data?.loads ?? [];

  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ name: string; content: string; size: number } | null>(
    null,
  );
  const [resultModal, setResultModal] = useState<RunDataLoadResponse | null>(null);
  const [historyModal, setHistoryModal] = useState<DataLoadRunDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const selectedSource = selectedSourceId
    ? sources.find((s) => s.id === selectedSourceId) ?? null
    : null;

  async function onFileChosen(file: File): Promise<void> {
    const content = await file.text();
    setPicked({ name: file.name, content, size: file.size });
  }

  async function submitLoad(): Promise<void> {
    if (!selectedSource || !picked) return;
    const res = await run.mutateAsync({
      source: selectedSource.id,
      filename: picked.name,
      content: picked.content,
    });
    setResultModal(res);
    setPicked(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Data loads</h2>
          <div className={styles.subtitle}>
            Import health data from third-party sources. Each loader knows how
            to parse its own file format and how to dedupe rows you've already
            uploaded — so re-running the same export is safe.
          </div>
        </div>
      </header>

      {/* Source picker */}
      <h3 style={{ margin: '8px 0 10px', fontSize: 14, fontWeight: 600 }}>
        Choose a source
      </h3>
      {sourcesQ.isLoading ? (
        <div className={styles.muted}>Loading sources…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {sources.map((s) => (
            <SourceCard
              key={s.id}
              source={s}
              selected={selectedSourceId === s.id}
              onPick={() => {
                setSelectedSourceId(s.id);
                setPicked(null);
              }}
            />
          ))}
        </div>
      )}

      {/* Uploader */}
      {selectedSource && (
        <div
          style={{
            marginTop: 18,
            padding: 16,
            border: '1px solid var(--line)',
            borderRadius: 8,
            background: 'var(--canvas)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <strong style={{ fontSize: 14 }}>{selectedSource.label}</strong>
            <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              → loads into <em>{selectedSource.target}</em>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label
              className="kos-btn"
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Icon name="plus" size={11} /> Choose file…
              <input
                ref={fileInputRef}
                type="file"
                accept={selectedSource.accept}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFileChosen(f);
                }}
              />
            </label>
            {picked && (
              <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                {picked.name}{' '}
                <span style={{ color: 'var(--ink-4)' }}>· {formatBytes(picked.size)}</span>
              </span>
            )}
            <button
              type="button"
              className="kos-btn kos-btn-primary"
              disabled={!picked || run.isPending}
              onClick={() => void submitLoad()}
              style={{ marginLeft: 'auto' }}
            >
              <Icon name="check" size={11} /> {run.isPending ? 'Loading…' : 'Upload'}
            </button>
          </div>
          {run.isError && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--neg)' }}>
              {run.error instanceof Error ? run.error.message : 'Upload failed.'}
            </div>
          )}
        </div>
      )}

      {/* History */}
      <h3 style={{ margin: '24px 0 10px', fontSize: 14, fontWeight: 600 }}>
        History{' '}
        <span style={{ fontFeatureSettings: "'tnum'", color: 'var(--ink-4)', fontWeight: 400 }}>
          {loads.length}
        </span>
      </h3>
      {loadsQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : loads.length === 0 ? (
        <div className={styles.empty}>No data loads yet.</div>
      ) : (
        <div className={styles.list}>
          {loads.map((l) => (
            <button
              key={l.id}
              type="button"
              className={styles.row}
              style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr auto auto',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                cursor: 'pointer',
                font: 'inherit',
                textAlign: 'left',
              }}
              onClick={() => setHistoryModal(l)}
            >
              <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                {formatDateTime(l.createdAt)}
              </span>
              <span>
                <div className={styles.rowTitle}>
                  {sources.find((s) => s.id === l.source)?.label ?? l.source}
                </div>
                <div className={styles.rowMeta}>
                  {l.filename || '(no filename)'} · {formatBytes(l.fileSize)}
                </div>
              </span>
              <SummaryPills summary={l.summary} compact />
              <StatusBadge status={l.status} />
            </button>
          ))}
        </div>
      )}

      {/* Modals */}
      {resultModal && (
        <SummaryModal
          title="Upload complete"
          source={sources.find((s) => s.id === resultModal.source)?.label ?? resultModal.source}
          status={resultModal.status}
          summary={resultModal.summary}
          onClose={() => setResultModal(null)}
        />
      )}
      {historyModal && (
        <SummaryModal
          title="Load summary"
          source={sources.find((s) => s.id === historyModal.source)?.label ?? historyModal.source}
          status={historyModal.status}
          summary={historyModal.summary}
          filename={historyModal.filename}
          createdAt={historyModal.createdAt}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
}

// ─── Source card ───────────────────────────────────────────────────────────

function SourceCard({
  source,
  selected,
  onPick,
}: {
  source: DataLoadSource;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      style={{
        textAlign: 'left',
        padding: '12px 14px',
        border: `1px solid ${selected ? 'var(--accent)' : 'var(--line)'}`,
        borderRadius: 8,
        background: selected
          ? 'color-mix(in oklab, var(--accent) 8%, var(--canvas))'
          : 'var(--canvas)',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600 }}>{source.label}</div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4 }}>
        {source.description}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
        {source.accept} → {source.target}
      </div>
    </button>
  );
}

// ─── Summary pills + status badge ─────────────────────────────────────────

function SummaryPills({ summary, compact }: { summary: DataLoadSummary; compact?: boolean }) {
  const Pill = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <span
      style={{
        fontSize: compact ? 11 : 12,
        padding: '2px 7px',
        borderRadius: 4,
        background: 'var(--surface-2)',
        color,
        whiteSpace: 'nowrap',
      }}
    >
      <strong style={{ fontFeatureSettings: "'tnum'" }}>{value}</strong> {label}
    </span>
  );
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <Pill label="created" value={summary.created} color="var(--accent)" />
      <Pill label="skipped" value={summary.skipped} color="var(--ink-3)" />
      <Pill label="errored" value={summary.errored} color="var(--neg)" />
    </span>
  );
}

function StatusBadge({ status }: { status: 'completed' | 'failed' | 'partial' }) {
  const meta = {
    completed: { label: 'Completed', color: 'var(--accent)' },
    partial: { label: 'Partial', color: 'var(--warn)' },
    failed: { label: 'Failed', color: 'var(--neg)' },
  }[status];
  return (
    <span
      style={{
        fontSize: 12,
        padding: '2px 8px',
        borderRadius: 4,
        background: 'var(--surface-2)',
        color: meta.color,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {meta.label}
    </span>
  );
}

// ─── Summary modal ────────────────────────────────────────────────────────

function SummaryModal({
  title,
  source,
  status,
  summary,
  filename,
  createdAt,
  onClose,
}: {
  title: string;
  source: string;
  status: 'completed' | 'failed' | 'partial';
  summary: DataLoadSummary;
  filename?: string;
  createdAt?: string;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 20, 40, 0.45)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 560,
          maxWidth: '92vw',
          maxHeight: '85vh',
          overflow: 'auto',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          padding: 20,
          boxShadow: 'var(--shadow-md)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <StatusBadge status={status} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          {source}
          {filename ? ` · ${filename}` : ''}
          {createdAt ? ` · ${formatDateTime(createdAt)}` : ''}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 16 }}>
          <Kpi label="Rows" value={summary.totalRows} />
          <Kpi label="Created" value={summary.created} accent="var(--accent)" />
          <Kpi label="Skipped" value={summary.skipped} />
          <Kpi label="Errored" value={summary.errored} accent="var(--neg)" />
        </div>

        {summary.errors.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 12,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 6,
              }}
            >
              Errors {summary.errors.length > 50 ? '· (showing first 50)' : ''}
            </div>
            <div
              style={{
                border: '1px solid var(--line)',
                borderRadius: 6,
                background: 'var(--surface-2)',
                maxHeight: 220,
                overflow: 'auto',
              }}
            >
              {summary.errors.map((e, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '50px 1fr',
                    gap: 10,
                    padding: '6px 10px',
                    fontSize: 13,
                    borderBottom: '1px solid var(--line-2, var(--line))',
                  }}
                >
                  <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
                    row {e.row}
                  </span>
                  <span style={{ color: 'var(--ink-2)' }}>{e.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="kos-btn kos-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: 'var(--surface-2)',
        padding: '10px 12px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          fontFeatureSettings: "'tnum'",
          color: accent ?? 'var(--ink)',
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Format helpers ──────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
